import { IUser } from '@common/types/user';
import { TableCellProps } from '@mui/material';
import { SvgIconProps } from '@mui/material/SvgIcon/SvgIcon';
import { TypographyProps } from '@mui/material/Typography/Typography';
import { ICONS } from '@navbar/utils';
import { ClassValue } from 'clsx';
import { PropsWithChildren, ReactElement } from 'react';
import { MessageDescriptor } from 'react-intl';
import { LinkProps } from 'react-router-dom';

type Order = 'asc' | 'desc';

interface FilterFn<T> {
    callback: (value: Partial<T>, filterValue?: string[], user?: IUser) => value is T;
}

type IDropzoneFile = {
    file: File;
    isLoading?: boolean;
    error?: string;
};

type FormStepList<T = object> = Array<FormStepProps & { fields: Array<keyof T> }>;

type FormStepProps = { title: string; description?: string };

interface Filter<T> extends FilterFn<T> {
    id: string;
    label?: string;
}

type LowercaseLetter =
    | 'a'
    | 'b'
    | 'c'
    | 'd'
    | 'e'
    | 'f'
    | 'g'
    | 'h'
    | 'i'
    | 'j'
    | 'k'
    | 'l'
    | 'm'
    | 'n'
    | 'o'
    | 'p'
    | 'q'
    | 'r'
    | 's'
    | 't'
    | 'u'
    | 'v'
    | 'w'
    | 'x'
    | 'y'
    | 'z';

type UppercaseLetter =
    | 'A'
    | 'B'
    | 'C'
    | 'D'
    | 'E'
    | 'F'
    | 'G'
    | 'H'
    | 'I'
    | 'J'
    | 'K'
    | 'L'
    | 'M'
    | 'N'
    | 'O'
    | 'P'
    | 'Q'
    | 'R'
    | 'S'
    | 'T'
    | 'U'
    | 'V'
    | 'W'
    | 'X'
    | 'Y'
    | 'Z';

type Alpha2CountryCode = `${UppercaseLetter}${UppercaseLetter}` | `${LowercaseLetter}${LowercaseLetter}`;

type LCAlpha2CountryCode = `${LowercaseLetter}${LowercaseLetter}`;

interface SelectOption<T extends string | number = string> {
    value: T;
    label: string;
    disabled?: boolean;
}

type BasicModalProps = PropsWithChildren<{
    open: boolean;
    setOpen: (value: boolean) => void;
    customCloseHandler?: () => void;
    width?: string | number;
}>;

type CustomModalProps = BasicModalProps & {
    dialogClassName: ClassValue;
    header: ReactElement;
    actions: ReactElement;
};

enum FlexDirection {
    ROW = 'row',
    COL = 'col',
}

type DropzoneFlexDirectionSettings = {
    md: FlexDirection;
    default: FlexDirection;
};

interface TableHeader<T> {
    id: keyof T;
    label: string;
    width?: TableCellProps['width'];
    disabled?: boolean;
    className?: string;
    labelProps?: TypographyProps;
    title?: string;
    sortable?: boolean;
}

interface DashboardMenuItem {
    label: string;
    icon: keyof typeof ICONS;
    iconClassName?: string;
    menuChildren?: DashboardMenuItem[];
    link?: {
        isReact?: boolean;
        href?: string;
        fn?: () => void;
        absolute?: boolean;
        target?: React.HTMLAttributeAnchorTarget;
    };
    disabled?: boolean;
    extraLabel?: { expanded: string; collapsed: string };
}

export type PublicMenuItem = {
    label: MessageDescriptor;
    to?: LinkProps['to'] | URL;
    icon: React.ComponentType<SvgIconProps>;
    disabled?: boolean;
};

interface TableRowOption<T> {
    label: string;
    color?: TypographyProps['color'];
    onClick?: (row: Partial<T>) => void;
    disabled?: (row: Partial<T>) => boolean;
    isLoading?: boolean;
    className?: string;
    items?: TableRowOption<T>[];
}

interface DialogInfo {
    title: string;
    primary: {
        label: string;
        onClick: () => void;
    };
    secondary: {
        label: string;
        onClick: () => void;
    };
}

type IconProps = { fill?: string; stroke?: string };

type SwappableScreens = 'main' | 'success';

type SwappableDialogRef = {
    swapScreen: (screenName: SwappableScreens) => void;
};

type Option<T extends string | number = string> = SelectOption<T> & {
    icon?: React.FunctionComponent<
        React.SVGProps<SVGSVGElement> & {
            title?: string;
        }
    >;
};

type TableRowOptionsDisabledState = {
    state: boolean;
    reason?: string;
};

export { FlexDirection };
export type {
    Alpha2CountryCode,
    BasicModalProps,
    CustomModalProps,
    DashboardMenuItem,
    DialogInfo,
    DropzoneFlexDirectionSettings,
    Filter,
    FilterFn,
    FormStepList,
    FormStepProps,
    IconProps,
    IDropzoneFile,
    LCAlpha2CountryCode,
    Option,
    Order,
    SelectOption,
    SwappableDialogRef,
    SwappableScreens,
    TableHeader,
    TableRowOption,
    TableRowOptionsDisabledState,
};
